// __BOARD_DRAFTS__ (2026-10-16, Henry) the hub's drafts as the FAR-LEFT column of the board ("Drafts"), the same drafts as
// the website's Bookings -> Drafts. A draft card reads like a job card (cardFace in routeBuilderModel.js: customer, then
// From -> To, the date / block when set) plus a DRAFT chip and who saved it, when. Twin of the Dispatch app's
// services/boardDrafts.ts (same rules); sim/boarddrafts.test.mjs runs it. Pure: no React, no Supabase.
import { faceLines } from './routeBuilderModel';

const LETTERS = 'ABCDEFGHIJ';
const str = (v) => (v == null ? '' : String(v)).trim();

// The draft's stops in order (empty boxes skipped), with the website's pickup / drop-off marks (A:P|B:D|C:PD).
export const draftStops = (d) => {
  const f = (d && d.fields) || {};
  const roles = {};
  str(f.addressRolesMap || f.addressRoles).split('|').forEach((p) => {
    const m = /^([A-J]):(P|D|PD)$/.exec(p.trim());
    if (m) roles[m[1]] = m[2];
  });
  const out = [];
  LETTERS.split('').forEach((l) => {
    const a = str(f['address' + l]);
    if (a) out.push({ letter: l, address: a, role: roles[l] || '' });
  });
  return out;
};

// The draft as a job card sees a load: pickups, then drop-offs, then unmarked stops.
export const draftAsLoad = (d) => ({
  customer_name: str(d && d.fields && d.fields.billingInfo),
  load_stops: draftStops(d).map((s, i) => ({
    stop_type: s.role === 'P' ? 'pickup' : s.role === 'D' ? 'delivery' : '',
    stop_sequence: i + 1,
    address: s.address,
  })),
});

const BLOCKS = { 1: ['1st', '7–9'], 2: ['2nd', '9–12'], 3: ['3rd', '12–3'], 4: ['4th', '3–5'] };
const SPAN = { 12: '7–12', 23: '9–3', 34: '12–5', 123: '7–3', 234: '9–5', 1234: '7–5' };
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "Thu Oct 16 · 2nd block 9–12" / "Thu Oct 16 · 09:00–11:00" / "" (nothing set: nothing shown).
export const draftWhen = (d) => {
  const f = (d && d.fields) || {};
  const parts = [];
  const date = str(f.jobDueDate || (d && d.due_date));
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (m) {
    const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    parts.push(DAYS[dt.getDay()] + ' ' + MONTHS[dt.getMonth()] + ' ' + dt.getDate());
  }
  const q = Array.from(new Set(str(Array.isArray(f.jobQuarters) ? f.jobQuarters.join('') : f.jobQuarters || f.jobQuarter)
    .replace(/[^1-4]/g, '').split('').filter(Boolean))).sort().join('');
  if (q.length === 1) parts.push(BLOCKS[q][0] + ' block ' + BLOCKS[q][1]);
  else if (q.length > 1 && SPAN[q]) parts.push(BLOCKS[q[0]][0] + '–' + BLOCKS[q[q.length - 1]][0] + ' block ' + SPAN[q]);
  else {
    const from = str(f.jobDueTime || (d && d.due_time));
    const to = str(f.pickupToTime);
    if (from) parts.push(to ? from + '–' + to : 'from ' + from);
  }
  return parts.join(' · ');
};

// "Saved by Sam · 2 h ago": who saved it (the hub names them) and when it was last saved.
export const draftSaved = (d, now = Date.now()) => {
  const who = str(d && d._user_name) || 'someone';
  const t = Date.parse(str(d && (d._updated_at || d.updated || d.created)));
  if (!Number.isFinite(t)) return 'Saved by ' + who;
  const min = Math.max(0, Math.round((now - t) / 60000));
  let ago;
  if (min < 1) ago = 'just now';
  else if (min < 60) ago = min + ' min ago';
  else if (min < 24 * 60) ago = Math.round(min / 60) + ' h ago';
  else { const dt = new Date(t); ago = MONTHS[dt.getMonth()] + ' ' + dt.getDate(); }
  return 'Saved by ' + who + ' · ' + ago;
};

// __SAVED_QUOTES__ (2026-10-16) a saved quote is a draft with kind 'quote' and a short ID the hub gave it (Q-7K3F).
export const quoteIdOf = (d) => {
  const q = str(d && d.quote_id).toUpperCase();
  return d && d.kind === 'quote' && /^Q-[A-Z0-9]{4,6}$/.test(q) ? q : '';
};
// A job booked from a saved quote keeps its ID in billing_data.quote_id (sometimes stored as a JSON string).
export const loadQuoteId = (load) => {
  let bd = load && load.billing_data;
  for (let i = 0; i < 2 && typeof bd === 'string'; i++) { try { bd = JSON.parse(bd); } catch (e) { return ''; } }
  const q = str(bd && bd.quote_id).toUpperCase();
  return /^Q-[A-Z0-9]{4,6}$/.test(q) ? q : '';
};
// What a search box may hold to find this ID: "q-7k3f", "q7k3f".
export const quoteSearch = (q) => (q ? [q.toLowerCase(), q.replace('-', '').toLowerCase()].join(' ') : '');
// "$1,240" (the price the quote was saved with, before FSC) or ''.
export const draftPrice = (d) => {
  const p = parseFloat(str(d && d.quoted_price).replace(/[$,]/g, ''));
  return Number.isFinite(p) && p > 0 ? '$' + Math.round(p).toLocaleString('en-CA') : '';
};
// All | Drafts | Quotes
export const DRAFT_KINDS = ['all', 'draft', 'quote'];
export const kindMatches = (d, kind) => (kind === 'quote' ? !!quoteIdOf(d) : kind === 'draft' ? !quoteIdOf(d) : true);

// Everything a draft card shows, the hover text, and what the board's search matches.
export const draftFace = (d, now = Date.now()) => {
  const load = draftAsLoad(d);
  const customer = str(load.customer_name) || 'No customer yet';
  const stops = draftStops(d);
  const kind = (r) => (r === 'P' ? 'Pickup' : r === 'D' ? 'Drop-off' : r === 'PD' ? 'Pickup + drop-off' : 'Stop');
  const quoteId = quoteIdOf(d);
  return {
    customer,
    lines: faceLines(load),
    when: draftWhen(d),
    saved: draftSaved(d, now),
    quoteId,
    price: draftPrice(d),
    tooltip: customer + '\n' + (quoteId ? 'Quote ' + quoteId + ' (not a job yet)' : 'Draft (not a job yet)') + '\n'
      + (stops.map((s, i) => (i + 1) + '. ' + kind(s.role) + ': ' + s.address).join('\n') || 'No stops yet'),
    search: [customer, ...stops.map((s) => s.address), str(d && d._user_name), quoteSearch(quoteId), quoteId ? 'quote' : ''].join(' ').toLowerCase(),
  };
};
